import React, { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * ClienteAutocomplete — combobox de clientes (users con role=cliente).
 * Crea nuevos clientes inline mediante un mini-dialog (email + password obligatorios).
 *
 * Props:
 *  - value: string (nombre actual)
 *  - cliente_id: string (id del user seleccionado)
 *  - onChange: ({ nombre, cliente_id, telefono }) => void
 *  - placeholder, testid
 */
export default function ClienteAutocomplete({
  value, cliente_id, onChange,
  placeholder = "Buscar cliente...", testid,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ nombre: "", email: "", password: "", telefono: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users", { params: { role: "cliente" } });
      setUsers(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSelect = (u) => {
    onChange({ nombre: u.nombre, cliente_id: u.id, telefono: u.telefono || "" });
    setOpen(false);
  };

  const openCreate = () => {
    setCreateForm({ nombre: search.trim(), email: "", password: "", telefono: "" });
    setOpen(false);
    setCreateOpen(true);
  };

  const save = async () => {
    if (!createForm.nombre || !createForm.email || !createForm.password) {
      toast.error("Completá nombre, email y contraseña.");
      return;
    }
    if (createForm.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/users", {
        nombre: createForm.nombre.trim(),
        email: createForm.email.trim().toLowerCase(),
        password: createForm.password,
        telefono: createForm.telefono,
        role: "cliente",
      });
      await load();
      onChange({ nombre: r.data.nombre, cliente_id: r.data.id, telefono: r.data.telefono });
      toast.success("Cliente creado.");
      setCreateOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear cliente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button" variant="outline" role="combobox" aria-expanded={open}
            data-testid={testid}
            className={`w-full justify-between bg-white border-[#E5E0D8] hover:bg-[#FBF8F2] h-10 font-normal ${value ? "text-[#1A1A1A]" : "text-[#8C8C8C]"}`}
          >
            <span className="flex-1 truncate text-left">{value || placeholder}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[280px]" align="start">
          <Command shouldFilter={true}>
            <CommandInput placeholder="Buscar o crear..." value={search} onValueChange={setSearch} data-testid={testid ? `${testid}-input` : undefined} />
            <CommandList>
              {loading && <div className="py-4 text-center text-sm text-[#8C8C8C]">Cargando...</div>}
              <CommandEmpty>
                {search.trim() ? (
                  <button type="button" onClick={openCreate} className="flex items-center gap-2 px-3 py-2 w-full text-left text-sm text-[#C85A32] hover:bg-[#F4EAE6] rounded-md" data-testid={testid ? `${testid}-create` : undefined}>
                    <Plus className="h-4 w-4" /> Crear cliente "{search.trim()}"
                  </button>
                ) : (
                  <span className="text-sm text-[#8C8C8C]">Sin clientes.</span>
                )}
              </CommandEmpty>
              <CommandGroup>
                {users.map((c) => (
                  <CommandItem key={c.id} value={c.nombre} onSelect={() => handleSelect(c)} data-testid={testid ? `${testid}-opt-${c.id}` : undefined}>
                    <Check className={`mr-2 h-4 w-4 ${cliente_id === c.id ? "opacity-100 text-[#C85A32]" : "opacity-0"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{c.nombre}</div>
                      <div className="text-[11px] text-[#8C8C8C] truncate">
                        <span className="font-mono">{c.email}</span>
                        {c.telefono && <> · {c.telefono}</>}
                      </div>
                    </div>
                  </CommandItem>
                ))}
                <CommandItem value="__create__" onSelect={openCreate} data-testid={testid ? `${testid}-create-row` : undefined}>
                  <Plus className="mr-2 h-4 w-4 text-[#C85A32]" />
                  <span className="text-sm text-[#C85A32]">{search.trim() ? `Crear "${search.trim()}"` : "Crear cliente nuevo"}</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader><DialogTitle>Crear nuevo cliente</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label>Nombre</Label>
              <Input value={createForm.nombre} onChange={(e) => setCreateForm({ ...createForm, nombre: e.target.value })} data-testid="cli-quickcreate-nombre" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} placeholder="cliente@email.com" data-testid="cli-quickcreate-email" />
            </div>
            <div>
              <Label>Contraseña (mínimo 6)</Label>
              <Input type="text" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder="••••••" data-testid="cli-quickcreate-password" />
            </div>
            <div>
              <Label>Teléfono (opcional)</Label>
              <Input value={createForm.telefono} onChange={(e) => setCreateForm({ ...createForm, telefono: e.target.value })} placeholder="+54 11..." data-testid="cli-quickcreate-tel" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#C85A32] hover:bg-[#A64A29] text-white" data-testid="cli-quickcreate-save">
              {saving ? "Creando..." : "Crear cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
